import { useState, useMemo, useCallback } from 'react'
import { RoomListType, RoomType, ReservationType } from '@/models/room-manager/response/guest-room-indicator'
import { ApprovedStatusType } from '@/models/reservation-approved-status'

/**
 * 部屋のハイライト表示を管理するカスタムフック
 * 同じ予約IDやチェックインIDを持つ部屋をホバー時にハイライト表示する
 */
export const useHoverGroupingForIndicator = (roomEachFloor: RoomListType[]) => {
  const [hoveredCheckinId, setHoveredCheckinId] = useState<string | null>(null)
  const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null)

  // 現在または次の予約情報を取得
  const getShowReservation = useCallback((room: RoomType): ReservationType | undefined => {
    if (room.currentReservation && room.currentReservation.roomAssignStatus !== ApprovedStatusType.Checkout) {
      return room.currentReservation
    }
    return room.nextReservation
  }, [])

  // 重複しているID（複数の部屋に割り当てられているID）を検出
  const duplicateIds = useMemo(() => {
    const allRooms = roomEachFloor.flatMap(floor => floor.rooms).filter((room): room is RoomType => !!room)

    const allReservations = allRooms.map(getShowReservation).filter((reservation): reservation is ReservationType => !!reservation)

    // 重複しているIDだけを抽出する関数
    const findDuplicateIds = <T>(items: T[], extractId: (item: T) => string | undefined) => {
      const idCounts = new Map<string, number>()

      items.forEach(item => {
        const id = extractId(item)
        if (id) idCounts.set(id, (idCounts.get(id) ?? 0) + 1)
      })

      return new Set(
        Array.from(idCounts)
          .filter(([, count]) => count >= 2)
          .map(([id]) => id),
      )
    }

    return {
      checkinIds: findDuplicateIds(allReservations, reservation => reservation.checkInId),
      reservationIds: findDuplicateIds(allReservations, reservation => reservation.reservationId),
    }
  }, [roomEachFloor, getShowReservation])

  const handleMouseEnter = useCallback((reservation: ReservationType | undefined) => {
    setHoveredReservationId(reservation?.reservationId ?? null)
    setHoveredCheckinId(reservation?.checkInId ?? null)
  }, [])

  // マウスリーブイベントハンドラ
  const handleMouseLeave = useCallback(() => {
    setHoveredReservationId(null)
    setHoveredCheckinId(null)
  }, [])

  // ハイライト判定関数
  const isHighlighted = useCallback(
    (reservation: ReservationType | undefined) => {
      if (!reservation) return false

      const { reservationId, checkInId } = reservation

      // reservationIdが一致し、かつ重複している場合
      if (reservationId && reservationId === hoveredReservationId && duplicateIds.reservationIds.has(reservationId)) {
        return true
      }

      // checkInIdが一致し、かつ重複している場合
      if (checkInId && checkInId === hoveredCheckinId && duplicateIds.checkinIds.has(checkInId)) {
        return true
      }

      return false
    },
    [hoveredReservationId, hoveredCheckinId, duplicateIds],
  )

  return {
    getShowReservation,
    isHighlighted,
    handleMouseEnter,
    handleMouseLeave,
  }
}
